import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onReset?: () => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to monitoring service
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo })
  }

  private handleReset = (): void => {
    this.props.onReset?.()
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  private handleGoHome = (): void => {
    window.location.href = '/'
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-[400px] items-center justify-center p-4">
          <div className="w-full max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>

            <h2 className="mt-4 text-lg font-semibold text-red-900">เกิดข้อผิดพลาด</h2>

            <p className="mt-2 text-sm text-red-700">
              ขออภัย เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง
            </p>

            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-xs text-red-600 hover:text-red-800">
                  รายละเอียดข้อผิดพลาด (สำหรับนักพัฒนา)
                </summary>
                <pre className="mt-2 max-h-32 overflow-auto rounded bg-red-100 p-2 text-xs text-red-800">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={this.handleReset}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2',
                  'text-sm font-medium text-white transition-colors',
                  'hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none'
                )}
              >
                <RefreshCw className="h-4 w-4" />
                ลองใหม่
              </button>

              <button
                onClick={this.handleReload}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2',
                  'text-sm font-medium text-red-700 transition-colors',
                  'border border-red-200 hover:bg-red-50',
                  'focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none'
                )}
              >
                โหลดหน้าใหม่
              </button>

              <button
                onClick={this.handleGoHome}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2',
                  'text-sm font-medium text-slate-700 transition-colors',
                  'border border-slate-200 hover:bg-slate-50',
                  'focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:outline-none'
                )}
              >
                <Home className="h-4 w-4" />
                หน้าหลัก
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * HOC to wrap components with error boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
): React.ComponentType<P> {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}
